import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Radio, BatteryLow, Thermometer, Signal, Pencil, X, RefreshCw, AlertTriangle, Trash2, ArrowLeftRight, SlidersHorizontal, Download } from 'lucide-react';
import { apiFetch } from '../api/client';
import type { HierarchyFilter } from './TreeView';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Device {
  phantom_code: string;
  machine_code: string;
  point_index: number;
  point_name: string | null;
  db_id: number;
  sensor_name: string | null;
  model: string | null;
  last_data_at: string | null;
  last_measurement_at: string | null;  // actual vibration data — more reliable than last_data_at (gateway heartbeat)
  battery_voltage: number | null;
  signal_strength: number | null;
  sensor_temperature: number | null;
  machine_name: string;
  company_name: string;
  area_name: string;
  db_name: string | null;
  axis_mapping: Record<string, string>;
}

type AxisLabel = 'H' | 'V' | 'A';
type PhysChannel = 'x' | 'y' | 'z';
type DeviceStatus = 'connected' | 'low_battery' | 'disconnected';
type StatusFilter = 'all' | 'connected' | 'low_battery' | 'disconnected';

interface ReviewDevicesProps {
  hierarchyFilter: HierarchyFilter | null;
  onClearFilter: () => void;
  /** Open the Point page for the point this sensor is mounted on. Resolved by
   *  (machine_code, point_index) — devices.point_name disagrees with axes.point_name
   *  on a handful of rows, so the index is the identity and the name is only a label. */
  onPointClick?: (machineCode: string, machineName: string, pointName: string | null, pointIndex: number) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseLastSeen(raw: string | null): Date | null {
  if (!raw) return null;
  return new Date(raw.replace(/\//g, '-'));
}

function realBattery(v: number | null): number | null {
  return v !== null && v > 0 ? v : null;
}
function realTemp(c: number | null): number | null {
  return c !== null && c > -100 ? c : null;
}
function realSignal(s: number | null): number | null {
  return s !== null && s !== 0 ? s : null;
}

function deviceStatus(d: Device): DeviceStatus {
  // Use last_measurement_at (actual vibration data) for connectivity — last_data_at is a gateway
  // heartbeat that updates even when a sensor is RF-disconnected from the measurement point.
  const effectiveLastSeen = d.last_measurement_at || d.last_data_at;
  const lastSeen = parseLastSeen(effectiveLastSeen);
  const daysSince = lastSeen ? (Date.now() - lastSeen.getTime()) / 86_400_000 : Infinity;
  if (daysSince > 5) return 'disconnected';
  const batt = realBattery(d.battery_voltage);
  if (batt !== null && batt < 2.6) return 'low_battery';
  return 'connected';
}

function fmtLastSeen(raw: string | null): { date: string; time: string } | null {
  if (!raw) return null;
  const d = parseLastSeen(raw);
  if (!d || isNaN(d.getTime())) return null;
  return { date: d.toLocaleDateString('en-CA'), time: d.toTimeString().slice(0, 8) };
}

function fmtTemp(c: number | null): string {
  const v = realTemp(c);
  return v === null ? '—' : `${((v * 9) / 5 + 32).toFixed(0)} °F`;
}

function fmtBattery(v: number | null): string {
  const b = realBattery(v);
  return b === null ? '—' : `${b.toFixed(2)} V`;
}

// ---------------------------------------------------------------------------
// CSV export — pure client-side. Serialises the already-loaded list exactly as
// the page currently shows it: the caller passes the filtered/searched/sorted
// array, and the columns below are the card's fields in display order.
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<DeviceStatus, string> = {
  connected: 'Connected',
  low_battery: 'Low battery',
  disconnected: 'Disconnected',
};

/** RFC 4180 quoting: wrap when the value holds a comma, quote, CR/LF, or edge
 *  whitespace; double any embedded quote. */
function csvCell(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  if (s === '') return '';
  return /[",\r\n]/.test(s) || s !== s.trim() ? `"${s.replace(/"/g, '""')}"` : s;
}

/** "YYYY-MM-DD HH:MM:SS" — the card shows date and time stacked; one cell here. */
function csvTimestamp(raw: string | null): string {
  const f = fmtLastSeen(raw);
  return f ? `${f.date} ${f.time}` : '';
}

/** Units live in the headers so the cells stay numeric for spreadsheets. */
const CSV_COLUMNS: { header: string; value: (d: Device) => unknown }[] = [
  { header: 'Status',            value: d => STATUS_LABEL[deviceStatus(d)] },
  { header: 'Device',            value: d => d.phantom_code },
  { header: 'Last Data',         value: d => csvTimestamp(d.last_measurement_at || d.last_data_at) },
  { header: 'Gateway Last Seen', value: d => csvTimestamp(d.last_data_at) },
  { header: 'Signal (dB)',       value: d => realSignal(d.signal_strength) ?? '' },
  { header: 'Temperature (°F)',  value: d => {
      const c = realTemp(d.sensor_temperature);
      return c === null ? '' : ((c * 9) / 5 + 32).toFixed(0);
    } },
  { header: 'Battery (V)',       value: d => {
      const b = realBattery(d.battery_voltage);
      return b === null ? '' : b.toFixed(2);
    } },
  { header: 'Model',             value: d => d.model ?? '' },
  { header: 'Name',              value: d => d.sensor_name ?? '' },
  { header: 'Axis Mapping',      value: d => {
      const m = d.axis_mapping ?? {};
      if (Object.keys(m).length !== 3) return '';
      return (['x', 'y', 'z'] as PhysChannel[])
        .map(ch => `${ch.toUpperCase()}→${(m as any)[ch]}`)
        .join(' · ');
    } },
  { header: 'Database',          value: d => d.db_name ?? '' },
  { header: 'Company',           value: d => d.company_name ?? '' },
  { header: 'Area',              value: d => d.area_name ?? '' },
  { header: 'Machine',           value: d => d.machine_name ?? '' },
  { header: 'Point',             value: d => d.point_name ?? '' },
];

function exportDevicesCsv(devices: Device[]) {
  const lines = [
    CSV_COLUMNS.map(c => csvCell(c.header)).join(','),
    ...devices.map(d => CSV_COLUMNS.map(c => csvCell(c.value(d))).join(',')),
  ];
  // Leading BOM so Excel reads the degree sign / arrows as UTF-8, CRLF per RFC 4180.
  const blob = new Blob(['﻿' + lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const now = new Date();
  const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = `review-devices-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Status dot
// ---------------------------------------------------------------------------

function StatusDot({ status }: { status: DeviceStatus }) {
  const colour =
    status === 'connected'   ? 'bg-green-500' :
    status === 'low_battery' ? 'bg-orange-400' : 'bg-red-500';
  return <span className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${colour} shadow-sm`} />;
}

function BatteryIcon({ v }: { v: number | null }) {
  const b = realBattery(v);
  if (b === null) return null;
  const cls = b < 2.2 ? 'text-red-500' : b < 2.6 ? 'text-orange-400' : 'text-gray-400';
  return <BatteryLow className={`w-5 h-5 ${cls}`} />;
}

// ---------------------------------------------------------------------------
// Axis mapping modal
// ---------------------------------------------------------------------------

const AXIS_LABELS: AxisLabel[] = ['H', 'V', 'A'];
const CHANNEL_LABELS: Record<PhysChannel, string> = { x: 'X', y: 'Y', z: 'Z' };

function AxisMappingModal({
  device,
  onClose,
  onSaved,
}: {
  device: Device;
  onClose: () => void;
  onSaved: (phantomCode: string, mapping: Record<string, string>) => void;
}) {
  const initial = (
    device.axis_mapping && Object.keys(device.axis_mapping).length === 3
      ? device.axis_mapping
      : { x: 'H', y: 'V', z: 'A' }
  ) as Record<PhysChannel, AxisLabel>;

  const [mapping, setMapping] = useState<Record<PhysChannel, AxisLabel>>(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setChannel = (ch: PhysChannel, axis: AxisLabel) => {
    setMapping(prev => {
      const current = prev[ch];
      const swapCh = (Object.entries(prev) as [PhysChannel, AxisLabel][]).find(([k, v]) => v === axis && k !== ch)?.[0];
      const next = { ...prev, [ch]: axis } as Record<PhysChannel, AxisLabel>;
      if (swapCh) next[swapCh] = current;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      await apiFetch(`/setup/devices/${device.phantom_code}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ axis_mapping: mapping }),
      });
      onSaved(device.phantom_code, mapping);
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Axis Mapping</h2>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{device.phantom_code}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-5">Map each physical sensor channel to its measurement axis.</p>

        <div className="space-y-3 mb-6">
          {(['x', 'y', 'z'] as PhysChannel[]).map(ch => (
            <div key={ch} className="flex items-center gap-3">
              <span className="w-6 text-sm font-semibold text-gray-600">{CHANNEL_LABELS[ch]}</span>
              <div className="flex gap-1">
                {AXIS_LABELS.map(axis => {
                  const active = mapping[ch] === axis;
                  return (
                    <button key={axis} type="button" onClick={() => setChannel(ch, axis)}
                      className={`w-10 py-1.5 rounded text-sm font-semibold border transition-colors ${
                        active
                          ? 'bg-[#00D4BE] border-[#00D4BE] text-white'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-[#00D4BE]/50'
                      }`}>
                      {axis}
                    </button>
                  );
                })}
              </div>
              <span className="text-xs text-gray-400 ml-1">→ {mapping[ch]}</span>
            </div>
          ))}
        </div>

        {err && <p className="text-xs text-red-500 mb-3">{err}</p>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-[#00D4BE] text-white rounded-lg hover:bg-[#00bfab] transition-colors disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Change sensor modal — field swap: register a new sensor on this point and
// retire the old one (POST /setup/devices/{code}/replace).
// ---------------------------------------------------------------------------

function ChangeSensorModal({
  device,
  onClose,
  onReplaced,
}: {
  device: Device;
  onClose: () => void;
  onReplaced: () => void;
}) {
  const [newCode, setNewCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const trimmed = newCode.trim();
  const valid = /^\d{6,}$/.test(trimmed) && trimmed !== device.phantom_code;

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      await apiFetch(`/setup/devices/${device.phantom_code}/replace`, {
        method: 'POST',
        body: JSON.stringify({ new_phantom_code: trimmed }),
      });
      onReplaced();
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Change failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-base font-semibold text-gray-800">Change Sensor</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Register a new sensor on this point and retire the current one. The axis mapping is carried
          over and vibration history (keyed to the point, not the sensor) is unaffected.
        </p>

        <div className="text-xs text-gray-500 mb-4 space-y-1">
          <div><span className="text-gray-400 uppercase tracking-wide mr-1">Current</span>
            <span className="font-mono text-gray-700">{device.phantom_code}</span></div>
          <div><span className="text-gray-400 uppercase tracking-wide mr-1">Point</span>
            <span className="text-gray-700">{[device.machine_name, device.point_name].filter(Boolean).join(' › ')}</span></div>
        </div>

        <label className="block text-xs font-medium text-gray-600 mb-1">New sensor phantom code</label>
        <input type="text" inputMode="numeric" value={newCode} autoFocus
          onChange={e => setNewCode(e.target.value.replace(/[^\d]/g, ''))}
          placeholder="e.g. 189322882"
          className="w-full px-3 py-2 text-sm font-mono bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D4BE]/60 transition-colors" />
        {trimmed && trimmed === device.phantom_code && (
          <p className="text-xs text-amber-600 mt-1">That is the current sensor.</p>
        )}

        {err && <p className="text-xs text-red-500 mb-3 mt-2">{err}</p>}
        <div className="flex gap-2 justify-end mt-5">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={!valid || saving}
            className="px-4 py-2 text-sm font-semibold bg-[#00D4BE] text-white rounded-lg hover:bg-[#00bfab] transition-colors disabled:opacity-50">
            {saving ? 'Changing…' : 'Change sensor'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete sensor confirmation (DELETE /setup/devices/{code})
// ---------------------------------------------------------------------------

function DeleteSensorModal({
  device,
  onClose,
  onDeleted,
}: {
  device: Device;
  onClose: () => void;
  onDeleted: (code: string) => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState('');

  const handleDelete = async () => {
    setDeleting(true); setErr('');
    try {
      await apiFetch(`/setup/devices/${device.phantom_code}`, { method: 'DELETE' });
      onDeleted(device.phantom_code);
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Delete failed');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-4 h-4 text-red-500" />
          </div>
          <h2 className="text-base font-semibold text-gray-800">Delete Sensor</h2>
        </div>
        <p className="text-sm text-gray-600 mb-1">
          Remove sensor <span className="font-mono text-gray-800">{device.phantom_code}</span> from this point?
        </p>
        <p className="text-xs text-gray-400 mb-2">{[device.machine_name, device.point_name].filter(Boolean).join(' › ')}</p>
        <p className="text-xs text-gray-500 mb-5">
          This removes only the sensor assignment. Vibration history and alarms (keyed to the point,
          not the sensor) are unaffected.
        </p>

        {err && <p className="text-xs text-red-500 mb-3">{err}</p>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleDelete} disabled={deleting}
            className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60">
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Device card
// ---------------------------------------------------------------------------

function DeviceCard({
  device,
  onEditAxis,
  onChangeSensor,
  onDelete,
  onOpenPoint,
}: {
  device: Device;
  onEditAxis: () => void;
  onChangeSensor: () => void;
  onDelete: () => void;
  onOpenPoint?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  // Fixed positioning (computed from the trigger rect) so the menu escapes the
  // card's `overflow-hidden` clip instead of being cut off inside it.
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setMenuOpen(true);
  };
  const status = deviceStatus(device);
  // Show actual measurement time; fall back to gateway timestamp only when no measurements exist
  const lastSeen = fmtLastSeen(device.last_measurement_at || device.last_data_at);
  // Show gateway timestamp as a secondary row when it's newer than actual measurement (stale sensor case)
  const gwSeen  = device.last_measurement_at ? fmtLastSeen(device.last_data_at) : null;
  const showGw  = gwSeen !== null && status === 'disconnected';
  const borderColour =
    status === 'connected'   ? 'border-gray-200' :
    status === 'low_battery' ? 'border-orange-200' : 'border-red-200';

  return (
    <div className={`bg-white rounded-xl border ${borderColour} shadow-sm mb-3 overflow-hidden`}>
      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
        <StatusDot status={status} />
        <Radio className="w-4 h-4 text-gray-400 flex-shrink-0" />
        {onOpenPoint ? (
          <button type="button" onClick={onOpenPoint}
            title={`Open point page — ${[device.machine_name, device.point_name].filter(Boolean).join(' › ')}`}
            className="font-bold text-base tracking-wide text-[#00D4BE] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4BE]/50 rounded">
            {device.phantom_code}
          </button>
        ) : (
          <span className="font-bold text-gray-800 text-base tracking-wide">{device.phantom_code}</span>
        )}

        <div className="ml-auto flex items-center gap-5 text-sm text-gray-500">
          {lastSeen && (
            <div className="text-right">
              <div className={`text-xs uppercase tracking-wide font-medium ${status === 'disconnected' ? 'text-red-400' : 'text-gray-400'}`}>
                Last data
              </div>
              <div className={`text-xs ${status === 'disconnected' ? 'text-red-500 font-medium' : 'text-gray-500'}`}>{lastSeen.date}</div>
              <div className={`text-xs font-mono ${status === 'disconnected' ? 'text-red-500' : 'text-gray-500'}`}>{lastSeen.time}</div>
              {showGw && gwSeen && (
                <div className="text-[10px] text-gray-400 mt-0.5">
                  <span className="text-gray-300">GW seen </span>{gwSeen.date}
                </div>
              )}
            </div>
          )}
          {realSignal(device.signal_strength) !== null && (
            <div className="flex items-center gap-1">
              <Signal className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium">{device.signal_strength} dB</span>
            </div>
          )}
          {realTemp(device.sensor_temperature) !== null && (
            <div className="flex items-center gap-1">
              <Thermometer className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium">{fmtTemp(device.sensor_temperature)}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <BatteryIcon v={device.battery_voltage} />
            <span className={`text-xs font-medium ${
              realBattery(device.battery_voltage) !== null && realBattery(device.battery_voltage)! < 2.2 ? 'text-red-500' :
              realBattery(device.battery_voltage) !== null && realBattery(device.battery_voltage)! < 2.6 ? 'text-orange-500' :
              'text-gray-500'
            }`}>
              {fmtBattery(device.battery_voltage)}
            </span>
          </div>
          <div>
            <button ref={btnRef} type="button" onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
              title="Sensor actions" aria-haspopup="menu" aria-expanded={menuOpen}
              className="p-1.5 rounded-lg text-gray-400 hover:text-[#00D4BE] hover:bg-[#00D4BE]/10 transition-colors">
              <Pencil className="w-4 h-4" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div role="menu"
                  className="fixed z-50 w-52 bg-white border border-gray-200 rounded-lg shadow-xl py-1 text-sm"
                  style={{ top: menuPos.top, right: menuPos.right }}>
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onEditAxis(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-gray-50 text-left">
                    <SlidersHorizontal className="w-4 h-4 text-gray-400" /> Edit axis mapping
                  </button>
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onChangeSensor(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-gray-50 text-left">
                    <ArrowLeftRight className="w-4 h-4 text-gray-400" /> Change sensor
                  </button>
                  <div className="my-1 border-t border-gray-100" />
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onDelete(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-red-600 hover:bg-red-50 text-left">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 py-3">
        <div className="flex items-center gap-8 text-sm text-gray-500 mb-3">
          <span><span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Model</span>{device.model ?? '—'}</span>
          <span><span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Name</span>{device.sensor_name ?? '—'}</span>
          {Object.keys(device.axis_mapping ?? {}).length === 3 && (
            <span className="flex items-center gap-1">
              <span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Axes</span>
              {(['x', 'y', 'z'] as PhysChannel[]).map((ch, i) => (
                <span key={ch} className="text-xs font-mono">
                  {i > 0 && <span className="text-gray-300 mx-0.5">·</span>}
                  <span className="text-gray-400">{ch.toUpperCase()}</span>
                  <span className="text-gray-300">→</span>
                  <span className="text-[#00D4BE] font-semibold">{(device.axis_mapping as any)[ch]}</span>
                </span>
              ))}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-500 flex-wrap">
          {[device.db_name, device.company_name, device.area_name, device.machine_name, device.point_name]
            .filter(Boolean).map((part, i, arr) => (
              <span key={i} className="flex items-center gap-1">
                <span className="text-gray-600">{part}</span>
                {i < arr.length - 1 && <span className="text-gray-300">›</span>}
              </span>
            ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status summary bar
// ---------------------------------------------------------------------------

function StatusBar({
  devices,
  statusFilter,
  onStatusFilter,
}: {
  devices: Device[];
  statusFilter: StatusFilter;
  onStatusFilter: (s: StatusFilter) => void;
}) {
  const counts = {
    connected:    devices.filter(d => deviceStatus(d) === 'connected').length,
    low_battery:  devices.filter(d => deviceStatus(d) === 'low_battery').length,
    disconnected: devices.filter(d => deviceStatus(d) === 'disconnected').length,
  };

  const pill = (s: StatusFilter, count: number, dot: string, label: string) => {
    const active = statusFilter === s;
    return (
      <button type="button" onClick={() => onStatusFilter(active ? 'all' : s)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
          active
            ? 'border-transparent shadow-sm ' + (
                s === 'connected'    ? 'bg-green-100 text-green-700' :
                s === 'low_battery' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700')
            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
        }`}>
        <span className={`w-2.5 h-2.5 rounded-full inline-block flex-shrink-0 ${dot}`} />
        {count} {label}
      </button>
    );
  };

  return (
    <div className="flex items-center gap-2 text-sm mb-4">
      {pill('connected',    counts.connected,    'bg-green-500',  'Connected')}
      {pill('low_battery',  counts.low_battery,  'bg-orange-400', 'Low battery')}
      {pill('disconnected', counts.disconnected, 'bg-red-500',    'Disconnected')}
      <span className="text-gray-400 ml-auto text-xs">Total: {devices.length}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ReviewDevices({ hierarchyFilter, onClearFilter, onPointClick }: ReviewDevicesProps) {
  const [allDevices, setAllDevices]     = useState<Device[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [changingSensor, setChangingSensor] = useState<Device | null>(null);
  const [deletingDevice, setDeletingDevice] = useState<Device | null>(null);
  const [lastUpdated, setLastUpdated]   = useState<Date | null>(null);
  const [refreshing, setRefreshing]     = useState(false);
  const [refreshStale, setRefreshStale] = useState(false);

  const loadAll = useCallback((showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    else setLoading(true);
    apiFetch('/setup/devices')
      .then((devs: Device[]) => {
        setAllDevices(devs);
        setLastUpdated(new Date());
        setError('');
        setRefreshStale(false);
      })
      .catch(() => setError('Failed to load devices.'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const id = setInterval(() => {
      apiFetch('/setup/devices').then((devs: Device[]) => {
        // Success: adopt fresh data, advance the "Updated" clock, clear any stale flag.
        setAllDevices(devs); setLastUpdated(new Date()); setRefreshStale(false);
      }).catch(() => {
        // W4: a background refresh failure must NOT advance "Updated" (that would
        // make stale data look fresh). Keep the last-good list, flag it as stale.
        setRefreshStale(true);
      });
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    let list = allDevices;

    if (hierarchyFilter) {
      list = list.filter(d => d.db_name === hierarchyFilter.dbName);
      if (hierarchyFilter.companyName) list = list.filter(d => d.company_name === hierarchyFilter.companyName);
      if (hierarchyFilter.areaName)    list = list.filter(d => d.area_name    === hierarchyFilter.areaName);
    }
    if (statusFilter !== 'all') {
      list = list.filter(d => deviceStatus(d) === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.phantom_code.includes(q) ||
        (d.sensor_name ?? '').toLowerCase().includes(q) ||
        d.machine_name.toLowerCase().includes(q) ||
        (d.point_name ?? '').toLowerCase().includes(q)
      );
    }
    if (statusFilter === 'low_battery') {
      list = [...list].sort((a, b) => (a.battery_voltage ?? 99) - (b.battery_voltage ?? 99));
    }
    return list;
  }, [allDevices, hierarchyFilter, statusFilter, search]);

  const visibleForStatusBar = useMemo(() => {
    let list = allDevices;
    if (hierarchyFilter) {
      list = list.filter(d => d.db_name === hierarchyFilter.dbName);
      if (hierarchyFilter.companyName) list = list.filter(d => d.company_name === hierarchyFilter.companyName);
      if (hierarchyFilter.areaName)    list = list.filter(d => d.area_name    === hierarchyFilter.areaName);
    }
    return list;
  }, [allDevices, hierarchyFilter]);

  const handleAxisMappingSaved = (phantomCode: string, mapping: Record<string, string>) => {
    setAllDevices(prev => prev.map(d => d.phantom_code === phantomCode ? { ...d, axis_mapping: mapping } : d));
  };

  // After a sensor swap the phantom_code changed and the new sensor's metadata
  // (battery/signal/name) differs — refetch rather than guess the new row.
  const handleReplaced = () => loadAll(true);

  const handleDeleted = (phantomCode: string) => {
    setAllDevices(prev => prev.filter(d => d.phantom_code !== phantomCode));
  };

  return (
    <>
      {editingDevice && (
        <AxisMappingModal
          device={editingDevice}
          onClose={() => setEditingDevice(null)}
          onSaved={handleAxisMappingSaved}
        />
      )}
      {changingSensor && (
        <ChangeSensorModal
          device={changingSensor}
          onClose={() => setChangingSensor(null)}
          onReplaced={handleReplaced}
        />
      )}
      {deletingDevice && (
        <DeleteSensorModal
          device={deletingDevice}
          onClose={() => setDeletingDevice(null)}
          onDeleted={handleDeleted}
        />
      )}

      <div className="flex flex-col h-full min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">
              {hierarchyFilter ? hierarchyFilter.label : 'All Devices'}
            </h1>
            {hierarchyFilter && (
              <button type="button" onClick={onClearFilter}
                className="text-xs text-[#00D4BE] hover:underline mt-0.5">
                ← All devices
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className={`text-xs ${refreshStale ? 'text-amber-600' : 'text-gray-400'}`}>
                Updated {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                {refreshStale && ' (stale)'}
              </span>
            )}
            <button type="button" onClick={() => loadAll(true)} disabled={refreshing} title="Refresh"
              className="p-1.5 rounded-lg text-gray-400 hover:text-[#00D4BE] hover:bg-[#00D4BE]/10 transition-colors disabled:opacity-40">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {/* Exports `filtered` — the same array the list renders, so the file
                carries the active hierarchy filter, status pill, search and sort. */}
            <button type="button" onClick={() => exportDevicesCsv(filtered)} disabled={filtered.length === 0}
              title={filtered.length ? `Export ${filtered.length} listed device${filtered.length === 1 ? '' : 's'} to CSV` : 'Nothing to export'}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:text-[#00D4BE] hover:border-[#00D4BE]/50 transition-colors disabled:opacity-40 disabled:hover:text-gray-600 disabled:hover:border-gray-200">
              <Download className="w-4 h-4" />
              Export
            </button>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by code, name, machine…"
              className="w-64 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D4BE]/60 transition-colors"
            />
          </div>
        </div>

        {loading && <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Loading devices…</div>}
        {error   && <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
        {refreshStale && !error && (
          <div className="text-amber-700 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Auto-refresh failed — showing the last successful load; data may be stale.
          </div>
        )}

        {!loading && !error && (
          <>
            <StatusBar devices={visibleForStatusBar} statusFilter={statusFilter} onStatusFilter={setStatusFilter} />
            <div className="flex-1 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No devices match the current filter.</div>
              )}
              {filtered.map(d => (
                <DeviceCard key={`${d.phantom_code}-${d.point_index}`} device={d}
                  onEditAxis={() => setEditingDevice(d)}
                  onChangeSensor={() => setChangingSensor(d)}
                  onDelete={() => setDeletingDevice(d)}
                  onOpenPoint={onPointClick
                    ? () => onPointClick(d.machine_code, d.machine_name, d.point_name, d.point_index)
                    : undefined}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
